import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  Trees, Waves, Footprints, Zap, Droplets, Trash2, Star, Fish,
  ShieldCheck, Sun, Mountain, ArrowUp, ArrowDown, Plus, X, PawPrint,
} from "lucide-react";
import { useEditMode } from "@/components/editor/EditModeContext";
import EditableText from "@/components/editor/EditableText";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  ShieldCheck, Waves, Footprints, Zap, Droplets, Trash2, Star, Fish, Trees, Sun, Mountain, PawPrint,
};
const ICON_NAMES = Object.keys(ICON_MAP);

const FALLBACK_FEATURES = [
  { icon: "ShieldCheck", title: "Exclusive Group Booking", desc: "3 private RV/trailer sites — booked as 1 group. The whole property is yours." },
  { icon: "Waves", title: "175 ft Rocky Shoreline", desc: "Bay of Fundy waterfront, right from your campsite trails." },
  { icon: "Footprints", title: "Wooded Hiking Trails", desc: "Walk through spruce and birch, down to the water's edge." },
  { icon: "Waves", title: "21-ft Tidal Range", desc: "Watch the highest tides on Earth go out and come back in." },
  { icon: "Zap", title: "Bring a Generator", desc: "No power hookup on site. Bring a generator for electricity. Quiet hours 10 PM – 7 AM." },
  { icon: "Droplets", title: "Self-Contained Required", desc: "No water hookup on site. RVs and campers must be fully self-contained." },
  { icon: "Star", title: "Zero Light Pollution", desc: "Exceptional stargazing. The Milky Way like you've never seen it." },
  { icon: "Fish", title: "Seals, Dolphins, Whales", desc: "Wildlife on your doorstep — plus fishing boats on the Bay." },
  { icon: "Trees", title: "Complete Privacy", desc: "No neighbours. No noise. One group at a time." },
  { icon: "PawPrint", title: "Dogs Welcome", desc: "Dogs are family here — on-leash or off, we trust you to know your pup. Heads up: rocky shoreline, 20-ft tides, and working land nearby." },
];

function FeatureCard({ feature, index, editMode, onUpdate, onDelete, onMove, total }) {
  const Icon = ICON_MAP[feature.icon] || Star;
  const titleRef = useRef(null);
  const descRef = useRef(null);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className={`relative flex gap-4 p-5 rounded-md bg-white/60 border border-wc-driftwood/30 ${editMode ? "group" : ""}`}
      data-testid={`feature-card-${index}`}
    >
      {/* Icon */}
      <div className="flex-shrink-0 w-10 h-10 rounded-sm bg-wc-teal/10 flex items-center justify-center relative">
        <Icon className="w-5 h-5 text-wc-teal" />
        {editMode && (
          <select
            value={feature.icon}
            onChange={(e) => onUpdate(feature.id, { icon: e.target.value })}
            className="absolute inset-0 opacity-0 cursor-pointer"
            title="Change icon"
          >
            {ICON_NAMES.map(name => <option key={name} value={name}>{name}</option>)}
          </select>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {editMode ? (
          <>
            <h3
              ref={titleRef}
              className="font-display text-wc-forest text-base font-semibold mb-1 outline-none cursor-text hover:outline-dashed hover:outline-1 hover:outline-offset-2 hover:outline-[#7A9E6E80] focus:outline-dashed focus:outline-2 focus:outline-offset-2 focus:outline-[#7A9E6E]"
              contentEditable
              suppressContentEditableWarning
              onBlur={() => {
                const t = titleRef.current?.innerText?.trim();
                if (t && t !== feature.title) onUpdate(feature.id, { title: t });
              }}
            >
              {feature.title}
            </h3>
            <p
              ref={descRef}
              className="font-body text-wc-slate text-sm leading-relaxed outline-none cursor-text hover:outline-dashed hover:outline-1 hover:outline-offset-2 hover:outline-[#7A9E6E80] focus:outline-dashed focus:outline-2 focus:outline-offset-2 focus:outline-[#7A9E6E]"
              contentEditable
              suppressContentEditableWarning
              onBlur={() => {
                const d = descRef.current?.innerText?.trim();
                if (d && d !== feature.desc) onUpdate(feature.id, { desc: d });
              }}
            >
              {feature.desc}
            </p>
          </>
        ) : (
          <>
            <h3 className="font-display text-wc-forest text-base font-semibold mb-1">{feature.title}</h3>
            <p className="font-body text-wc-slate text-sm leading-relaxed">{feature.desc}</p>
          </>
        )}
      </div>

      {/* Edit controls */}
      {editMode && (
        <div className="absolute -top-2 -right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {index > 0 && (
            <button onClick={() => onMove(index, index - 1)}
              className="w-6 h-6 rounded-full bg-wc-forest text-white flex items-center justify-center text-xs shadow" title="Move up">
              <ArrowUp className="w-3 h-3" />
            </button>
          )}
          {index < total - 1 && (
            <button onClick={() => onMove(index, index + 1)}
              className="w-6 h-6 rounded-full bg-wc-forest text-white flex items-center justify-center text-xs shadow" title="Move down">
              <ArrowDown className="w-3 h-3" />
            </button>
          )}
          <button onClick={() => onDelete(feature.id)}
            className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center text-xs shadow" title="Remove">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
    </motion.div>
  );
}

export default function Property() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const { editMode, authHeader } = useEditMode();
  const [features, setFeatures] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const fetchFeatures = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/features/public`);
      setFeatures(res.data.features.length > 0 ? res.data.features : FALLBACK_FEATURES);
    } catch {
      setFeatures(FALLBACK_FEATURES);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { fetchFeatures(); }, [fetchFeatures]);

  const updateFeature = async (id, updates) => {
    if (!id) return;
    setFeatures(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
    try { await axios.put(`${API}/admin/features/${id}`, updates, authHeader); } catch (e) { console.error(e); }
  };

  const deleteFeature = async (id) => {
    setFeatures(prev => prev.filter(f => f.id !== id));
    try { await axios.delete(`${API}/admin/features/${id}`, authHeader); } catch (e) { console.error(e); }
  };

  const addFeature = async () => {
    try {
      const res = await axios.post(`${API}/admin/features`, {
        icon: "Star", title: "New Feature", desc: "Click to edit this description.",
      }, authHeader);
      setFeatures(prev => [...prev, res.data.feature]);
    } catch (e) { console.error(e); }
  };

  const moveFeature = async (fromIdx, toIdx) => {
    const next = [...features];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setFeatures(next);
    const order = next.map(f => f.id).filter(Boolean);
    try { await axios.put(`${API}/admin/features/reorder`, { order }, authHeader); } catch (e) { console.error(e); }
  };

  if (!loaded) return null;

  return (
    <section id="property" data-testid="property-section" className="wc-section bg-wc-cream pt-6 sm:pt-8" ref={ref}>
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {features.map((f, i) => (
            <FeatureCard
              key={f.id || i}
              feature={f}
              index={i}
              editMode={editMode}
              onUpdate={updateFeature}
              onDelete={deleteFeature}
              onMove={moveFeature}
              total={features.length}
            />
          ))}
        </div>

        {editMode && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={addFeature}
            className="mt-4 w-full py-3 border-2 border-dashed border-wc-brass/40 rounded-md text-wc-brass font-body text-sm flex items-center justify-center gap-2 hover:border-wc-brass hover:bg-wc-brass/5 transition-all"
            data-testid="add-feature-button"
          >
            <Plus className="w-4 h-4" /> Add Feature
          </motion.button>
        )}
      </div>
    </section>
  );
}
